import React, { useEffect, useMemo, useState, useCallback } from "react";
import TableContainer from "../../BaseComponents/BaseTable";
import BaseModal from "../../BaseComponents/BaseModal";
import { toast } from "react-toastify";
import { checkStatusCodeSuccess, handleArrayResponse } from "../../Constant/common";
import {
  Action,
  changeStatus,
  closedDate,
  Company,
  Contact,
  Edit,
  dealDescription,
  Save,
  Search,
  status,
  Update,
  updateDealStatus,
  Delete,
} from "../../Constant";
import BaseButton from "../../BaseComponents/BaseButton";
import BaseSelect from "../../BaseComponents/BaseSelect";
import BaseDatePicker from "../../BaseComponents/BaseDatePicker";
import BaseTooltip from "../../BaseComponents/BaseTooltip";
import { deleteDeal, updateDealStatusApi } from "../../Api/dealApi";
import {
  ACCESSOR_KEYS,
  addDealLabel,
  addedByLabel,
  companyLabel,
  contactNameLabel,
  DEAL_STATUS_BADGE_MAP,
  dealLabel,
  dealRemark,
  dealStatusLabel,
  dealTypeLabel,
  DeleteDealConfirm,
  editDeal,
} from "../../Constant/Deal";
import { listOfCompanyApi } from "../../Api/companyApi";
import { listOfContactApi } from "../../Api/contactApi";
import * as yup from "yup";
import { useFormik } from "formik";
import { SelectPlaceHolder, validationMessages } from "../../Constant/validation";
import { createDealApi, listOfDealApi, updateDealApi, viewDeal } from "../../Api/dealApi";
import BaseInput from "../../BaseComponents/BaseInput";
import { addedBy, connectedProfile, LEAD_TYPE_OPTIONS, truncateText } from "../../Constant/Lead";
import { listOfSchedulerwApi } from "../../Api/interviewApi";
import { listOfConnectedProfileApi } from "../../Api/leadApi";
import { useSelectedCenter } from "../CenterContext";
import { usePageLoader } from "../../_helper/loading/LoadingContext";
import { Plus, TrendingUp, Edit2, Trash2, AlertCircle, Briefcase } from "react-feather";

/* ── Status badge config ── */
const STATUS_CFG = {
  Active: { color: "#6366f1", bg: "rgba(99,102,241,0.12)" },
  "On Hold": { color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  "At Risk": { color: "#f97316", bg: "rgba(249,115,22,0.12)" },
  Completed: { color: "#10b981", bg: "rgba(16,185,129,0.12)" },
  Closed: { color: "#f43f5e", bg: "rgba(244,63,94,0.12)" },
};

const dealValidationSchema = yup.object({
  company_id: yup.number().required(validationMessages.required(Company)),
  contact_id: yup.number().required(validationMessages.required(Contact)),
  deal_description: yup.string().required(validationMessages.required(dealDescription)),
});

const TipCell = (val, max = 22, color = "#6366f1") => {
  const t = truncateText(val, max);
  return val && val.length > max ? (
    <BaseTooltip label={val} position="top" maxWidth={280} color={color}>
      <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>{t}</span>
    </BaseTooltip>
  ) : (
    <span>{val || "—"}</span>
  );
};

const Deal = () => {
  document.title = dealLabel;
  const { setLoading } = usePageLoader();
  const { selectedCenterPayload } = useSelectedCenter();

  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(5);
  const [searchValue, setSearchValue] = useState("");
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [dealData, setDealData] = useState([]);
  const [dealSearchData, setDealSearchData] = useState([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusFormData, setStatusFormData] = useState({
    id: null,
    deal_status: "",
    deal_remarks: "",
  });
  const [idToDelete, setIdToDelete] = useState();
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [contactList, setContactList] = useState([]);
  const [addedByList, setAddedByList] = useState([]);
  const [profileList, setProfileList] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const [formData] = useState({
    company_id: "",
    contact_id: "",
    deal_description: "",
    deal_type: "",
    closed_date: new Date().toISOString().split("T")[0],
    added_by: "",
    connection_profile_id: "",
  });

  /* ── Formik ── */
  const dealFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      company_id: formData.company_id || "",
      contact_id: formData.contact_id || "",
      connection_profile_id: formData.connection_profile_id || "",
      added_by: formData.added_by || "",
      deal_description: formData.deal_description || "",
      deal_type: formData.deal_type || "",
      closed_date: formData.closed_date || "",
    },
    validationSchema: dealValidationSchema,
    onSubmit: async (values) => {
      setModalLoading(true);
      try {
        const payload = {
          ...values,
          company_id: Number(values.company_id),
          contact_id: Number(values.contact_id),
          connection_profile_id: Number(values.connection_profile_id),
          added_by: Number(values.added_by),
        };
        const response =
          isEditMode && selectedDealId
            ? await updateDealApi({ ...payload, id: selectedDealId })
            : await createDealApi(payload);
        if (checkStatusCodeSuccess(response?.statusCode)) {
          toast.success(response?.message);
          setShowAddModal(false);
          searchValue ? fetchSearchDealPermission() : fetchDealPermission();
        } else throw new Error(response?.message);
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message);
      } finally {
        setModalLoading(false);
      }
    },
  });

  /* ── Fetch helpers ── */
  const fetchCompanies = useCallback(async () => {
    try {
      const r = await listOfCompanyApi({});
      if (checkStatusCodeSuccess(r?.statusCode))
        setCompanyList((r?.data || []).map((c) => ({ value: c.id, label: c.company_name })));
    } catch (e) {
      return e;
    }
  }, []);
  const fetchContacts = useCallback(async () => {
    try {
      const r = await listOfContactApi({});
      if (checkStatusCodeSuccess(r?.statusCode))
        setContactList((r?.data || []).map((c) => ({ value: c.id, label: c.contact_name })));
    } catch (e) {
      return e;
    }
  }, []);
  const fetchAddedBy = useCallback(async () => {
    try {
      const r = await listOfSchedulerwApi({ center_id: selectedCenterPayload.center_id });
      if (checkStatusCodeSuccess(r?.statusCode))
        setAddedByList((r?.data || []).map((a) => ({ value: a.id, label: a.name })));
    } catch (e) {
      return e;
    }
  }, [selectedCenterPayload.center_id]);
  const fetchConnectedProfile = useCallback(async () => {
    try {
      const r = await listOfConnectedProfileApi({});
      if (checkStatusCodeSuccess(r?.statusCode))
        setProfileList((r?.data || []).map((p) => ({ value: p.id, label: p.profile_name })));
    } catch (e) {
      return e;
    }
  }, []);

  const openAddModal = () => {
    setIsEditMode(false);
    setSelectedDealId(null);
    dealFormik.resetForm();
    fetchCompanies();
    fetchContacts();
    fetchAddedBy();
    fetchConnectedProfile();
    setShowAddModal(true);
  };

  /* ── Deal list ── */
  const fetchSearchDealPermission = useCallback(() => {
    setLoading(true);
    listOfDealApi({
      order: [columnName, sortOrder],
      search: searchValue,
      pageNumber: currentPage,
      pageSize: customPageSize,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setDealSearchData(resp?.data?.listOfDeals);
          setTotalRecords(resp?.data?.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
        } else setDealSearchData([]);
      })
      .catch(() => setDealSearchData([]))
      .finally(() => setLoading(false));
  }, [columnName, sortOrder, currentPage, customPageSize, searchValue, setLoading]);

  const fetchDealPermission = useCallback(() => {
    setLoading(true);
    listOfDealApi({
      order: [columnName, sortOrder],
      pageNumber: currentPage,
      pageSize: customPageSize,
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setDealData(resp?.data?.listOfDeals);
          setTotalRecords(resp.data.totalRecordsCount);
          setTotalPages(resp?.data?.totalPages);
          setTotalNumberOfRows(resp?.data?.numberOfRows);
          setCurrentPage(resp?.data?.currentPage);
          if (searchValue) fetchSearchDealPermission();
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [
    columnName,
    sortOrder,
    currentPage,
    customPageSize,
    searchValue,
    fetchSearchDealPermission,
    setLoading,
  ]);

  useEffect(() => {
    searchValue ? fetchSearchDealPermission() : fetchDealPermission();
  }, [
    currentPage,
    customPageSize,
    sortOrder,
    columnName,
    searchValue,
    fetchDealPermission,
    fetchSearchDealPermission,
  ]);

  /* ── Edit ── */
  const handleEditDeal = useCallback(
    async (id) => {
      try {
        setModalLoading(true);
        const response = await viewDeal(id);
        if (response?.message) {
          const d = response.message;
          setSelectedDealId(id);
          setIsEditMode(true);
          dealFormik.setValues({
            company_id: d.company?.id || "",
            contact_id: d.contact?.id || "",
            deal_description: d.deal_description || "",
            deal_type: d.deal_type || "",
            connection_profile_id: d.connectionProfile?.id || "",
            added_by: d.authUser?.id || "",
            closed_date: d.closed_date || new Date().toISOString().split("T")[0],
          });
          await Promise.all([
            fetchCompanies(),
            fetchContacts(),
            fetchAddedBy(),
            fetchConnectedProfile(),
          ]);
          setShowAddModal(true);
        } else toast.error(response?.message);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setModalLoading(false);
      }
    },
    [dealFormik, fetchAddedBy, fetchCompanies, fetchConnectedProfile, fetchContacts],
  );

  /* ── Delete ── */
  const deleteEntry = async () => {
    try {
      setDeleteLoader(true);
      const resp = await deleteDeal(idToDelete);
      if (checkStatusCodeSuccess(resp?.statusCode)) {
        toast.success(resp?.message);
        searchValue ? fetchSearchDealPermission() : fetchDealPermission();
        setDeleteModal(false);
      } else toast.error(resp?.message);
    } catch (err) {
      handleArrayResponse(err);
    } finally {
      setDeleteLoader(false);
    }
  };

  /* ── Status ── */
  const handleStatusChangeClick = useCallback((deal) => {
    setStatusFormData({
      id: deal.id,
      deal_status: deal.deal_status || "",
      deal_remarks: deal.deal_remarks || "",
    });
    setShowStatusModal(true);
  }, []);

  const handleStatusSubmit = async () => {
    setModalLoading(true);
    try {
      const response = await updateDealStatusApi(statusFormData);
      if (checkStatusCodeSuccess(response?.statusCode)) {
        toast.success(response?.message);
        setShowStatusModal(false);
        searchValue ? fetchSearchDealPermission() : fetchDealPermission();
      } else throw new Error(response?.message);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setModalLoading(false);
    }
  };

  /* ── Columns ── */
  const columns = useMemo(
    () => [
      {
        header: ACCESSOR_KEYS.SRNOLABEL,
        accessorKey: ACCESSOR_KEYS.SERIAL_NUMBER,
        cell: (c) => (currentPage - 1) * customPageSize + c.row.index + 1,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: companyLabel,
        accessorKey: ACCESSOR_KEYS.COMPANY,
        cell: ({ row }) => TipCell(row?.original?.company?.company_name, 22, "#6366f1"),
        enableColumnFilter: false,
      },
      {
        header: contactNameLabel,
        accessorKey: ACCESSOR_KEYS.CONTACT_NAME,
        cell: ({ row }) => TipCell(row?.original?.contact?.contact_name, 22, "#8b5cf6"),
        enableColumnFilter: false,
        enableSorting: false,
      },
      { header: dealTypeLabel, accessorKey: ACCESSOR_KEYS.DEAL_TYPE, enableColumnFilter: false },
      {
        header: addedByLabel,
        accessorKey: ACCESSOR_KEYS.ADDED_BY,
        cell: ({ row }) => TipCell(row?.original?.authUser?.name, 20, "#06b6d4"),
        enableColumnFilter: false,
      },
      { header: closedDate, accessorKey: ACCESSOR_KEYS.CLOSED_DATE, enableColumnFilter: false },
      {
        header: dealDescription,
        accessorKey: ACCESSOR_KEYS.DEAL_DESCRIPTION,
        enableColumnFilter: false,
        cell: (c) => {
          const desc = c.row.original?.deal_description;
          const t = truncateText(desc, 30);
          return desc && desc.length > 30 ? (
            <BaseTooltip label={desc} position="top" maxWidth={280} color="#6366f1">
              <span style={{ cursor: "default", borderBottom: "1px dashed var(--glass-border)" }}>
                {t}
              </span>
            </BaseTooltip>
          ) : (
            <span>{t}</span>
          );
        },
      },
      {
        header: dealStatusLabel,
        accessorKey: ACCESSOR_KEYS.DEAL_STATUS,
        enableColumnFilter: false,
        enableSorting: false,
        cell: (c) => {
          const s = c.row.original?.deal_status;
          const cfg = STATUS_CFG[s] || { color: "#64748b", bg: "rgba(100,116,139,0.12)" };
          return (
            <span
              style={{
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: "0.6875rem",
                fontWeight: 700,
                background: cfg.bg,
                color: cfg.color,
                whiteSpace: "nowrap",
              }}
            >
              {s || "—"}
            </span>
          );
        },
      },
      {
        header: Action,
        accessorKey: ACCESSOR_KEYS.ACTION,
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
            <BaseTooltip label={changeStatus} color="#047857">
              <button
                onClick={() => handleStatusChangeClick(row.original)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(16,185,129,0.08)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  color: "#10b981",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <TrendingUp size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={Edit} color="#b45309">
              <button
                onClick={() => handleEditDeal(row.original?.id)}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(245,158,11,0.08)",
                  border: "1px solid rgba(245,158,11,0.2)",
                  color: "#f59e0b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Edit2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
            <BaseTooltip label={Delete} color="#be123c">
              <button
                onClick={() => {
                  setIdToDelete(row.original?.id);
                  setDeleteModal(true);
                }}
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 8,
                  background: "rgba(244,63,94,0.08)",
                  border: "1px solid rgba(244,63,94,0.2)",
                  color: "#f43f5e",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <Trash2 size={13} strokeWidth={2} />
              </button>
            </BaseTooltip>
          </div>
        ),
      },
    ],
    [currentPage, customPageSize, handleEditDeal, handleStatusChangeClick],
  );

  return (
    <div className="animate-fade-in" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* ── Page header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Briefcase size={20} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                fontSize: "1.375rem",
                fontWeight: 800,
                margin: 0,
                color: "var(--text-primary)",
              }}
            >
              {dealLabel}
            </h1>
            <p
              style={{ margin: 0, fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}
            >
              {totalRecords > 0 ? `${totalRecords} deals` : "Manage your deals"}
            </p>
          </div>
        </div>
        <BaseButton
          color="primary"
          onClick={openAddModal}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <Plus size={15} strokeWidth={2.5} /> {addDealLabel}
        </BaseButton>
      </div>

      {/* ── Table ── */}
      <div
        className="animate-slide-up"
        style={{
          background: "var(--glass-bg)",
          border: "1px solid var(--glass-border)",
          borderRadius: 16,
          overflow: "hidden",
          padding: "16px",
        }}
      >
        {dealData?.length > 0 || dealSearchData?.length > 0 ? (
          <TableContainer
            totalPages={totalPages}
            totalRecords={totalRecords}
            totalNumberOfRows={totalNumberOfRows}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            fetchData={(p) => setCurrentPage(p)}
            customPageSize={customPageSize}
            setCustomPageSize={setCustomPageSize}
            columns={columns}
            shouldLineBreak={false}
            data={searchValue ? dealSearchData : dealData || []}
            onSearch={(v) => {
              if (v !== searchValue) {
                setCurrentPage(1);
                setSearchValue(v);
              }
            }}
            fetchSortingData={(p, id, order) => {
              setCurrentPage(p);
              setColumnName(id);
              setSortOrder(order);
            }}
            SearchPlaceholder={Search}
            isGlobalFilter
            manualPagination
            manualFiltering
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "60px 20px",
              gap: 12,
            }}
          >
            <AlertCircle size={40} style={{ color: "var(--text-muted)", opacity: 0.4 }} />
            <p style={{ color: "var(--text-muted)", margin: 0 }}>No deals found</p>
          </div>
        )}
      </div>

      {/* ── Status modal ── */}
      <BaseModal
        isOpen={showStatusModal}
        toggler={() => setShowStatusModal(false)}
        title={updateDealStatus}
        size="md"
        submit={handleStatusSubmit}
        loader={modalLoading}
        submitText={Update}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <BaseSelect
            label={status}
            name="deal_status"
            value={statusFormData.deal_status}
            options={Object.keys(DEAL_STATUS_BADGE_MAP).map((s) => ({ value: s, label: s }))}
            handleChange={(name, value) => setStatusFormData((p) => ({ ...p, [name]: value }))}
            placeholder={SelectPlaceHolder(status)}
            required
          />
          <BaseInput
            type="textarea"
            label={dealRemark}
            name="deal_remarks"
            value={statusFormData.deal_remarks}
            handleChange={(e) => setStatusFormData((p) => ({ ...p, deal_remarks: e.target.value }))}
          />
        </div>
      </BaseModal>

      {/* ── Add / Edit modal ── */}
      <BaseModal
        isOpen={showAddModal}
        toggler={() => {
          if (!modalLoading) {
            dealFormik.resetForm();
            setIsEditMode(false);
            setSelectedDealId(null);
            setShowAddModal(false);
          }
        }}
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 9,
                background: "rgba(99,102,241,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {isEditMode ? (
                <Edit2 size={14} strokeWidth={2} style={{ color: "var(--brand-indigo)" }} />
              ) : (
                <Plus size={14} strokeWidth={2.5} style={{ color: "var(--brand-indigo)" }} />
              )}
            </div>
            <span>{isEditMode ? editDeal : addDealLabel}</span>
          </div>
        }
        submitText={isEditMode ? Update : Save}
        disabled={modalLoading}
        loader={modalLoading}
        submit={dealFormik.handleSubmit}
        size="lg"
      >
        <form onSubmit={dealFormik.handleSubmit} noValidate>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "0 16px",
            }}
          >
            <BaseSelect
              id="company_id"
              name="company_id"
              label={companyLabel}
              options={companyList}
              value={dealFormik.values.company_id}
              touched={dealFormik.touched.company_id}
              handleChange={(name, value) => dealFormik.setFieldValue(name, value)}
              handleBlur={dealFormik.handleBlur}
              error={dealFormik.touched.company_id && dealFormik.errors.company_id}
              required
            />
            <BaseSelect
              id="contact_id"
              name="contact_id"
              label={contactNameLabel}
              options={contactList}
              value={dealFormik.values.contact_id}
              handleChange={(name, value) => dealFormik.setFieldValue(name, value)}
              handleBlur={dealFormik.handleBlur}
              touched={dealFormik.touched.contact_id}
              error={dealFormik.touched.contact_id && dealFormik.errors.contact_id}
              required
            />
            <BaseSelect
              id="deal_type"
              name="deal_type"
              label={dealTypeLabel}
              options={LEAD_TYPE_OPTIONS}
              value={dealFormik.values.deal_type}
              handleChange={(name, value) => dealFormik.setFieldValue(name, value)}
              handleBlur={dealFormik.handleBlur}
              touched={dealFormik.touched.deal_type}
              error={dealFormik.touched.deal_type && dealFormik.errors.deal_type}
            />
            <BaseSelect
              id="connection_profile_id"
              name="connection_profile_id"
              label={connectedProfile}
              options={profileList}
              value={dealFormik.values.connection_profile_id}
              handleChange={(name, value) => dealFormik.setFieldValue(name, value)}
              handleBlur={dealFormik.handleBlur}
              touched={dealFormik.touched.connection_profile_id}
              error={
                dealFormik.touched.connection_profile_id && dealFormik.errors.connection_profile_id
              }
            />
            <BaseSelect
              id="added_by"
              name="added_by"
              label={addedBy}
              options={addedByList}
              value={dealFormik.values.added_by}
              handleChange={(name, value) => dealFormik.setFieldValue(name, value)}
              handleBlur={dealFormik.handleBlur}
              touched={dealFormik.touched.added_by}
              error={dealFormik.touched.added_by && dealFormik.errors.added_by}
            />
            <BaseDatePicker
              name="closed_date"
              label={closedDate}
              value={dealFormik.values.closed_date}
              onChange={(v) => dealFormik.setFieldValue("closed_date", v)}
              onBlur={dealFormik.handleBlur}
              touched={dealFormik.touched.closed_date}
              error={dealFormik.touched.closed_date && dealFormik.errors.closed_date}
            />
            <div style={{ gridColumn: "1 / -1" }}>
              <BaseInput
                label={dealDescription}
                type="textarea"
                id="deal_description"
                name="deal_description"
                rows="3"
                value={dealFormik.values.deal_description}
                handleChange={dealFormik.handleChange}
                handleBlur={dealFormik.handleBlur}
                touched={dealFormik.touched.deal_description}
                error={dealFormik.touched.deal_description && dealFormik.errors.deal_description}
                required
              />
            </div>
          </div>
        </form>
      </BaseModal>

      {/* ── Delete modal ── */}
      <BaseModal
        isOpen={deleteModal}
        title={`${Delete} ${dealLabel}`}
        toggler={() => setDeleteModal(false)}
        submit={deleteEntry}
        submitText={Delete}
        disabled={deleteLoader}
        loader={deleteLoader}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12, padding: "4px 0" }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(244,63,94,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Trash2 size={16} strokeWidth={2} style={{ color: "#f43f5e" }} />
          </div>
          <p
            style={{
              margin: 0,
              fontSize: "0.9375rem",
              color: "var(--text-primary)",
              lineHeight: 1.5,
            }}
          >
            {DeleteDealConfirm}
          </p>
        </div>
      </BaseModal>
    </div>
  );
};

export default Deal;
